import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppLanguage } from '@/hooks/useAppLanguage';
import { useCategoriesByType } from '@/hooks/useCategories';
import { articlesApi } from '@/lib/api';
import Colors from '@/constants/colors';
import { getTranslation } from '@/constants/translations';
import PageContainer from '@/components/PageContainer';
import PageHeader from '@/components/PageHeader';
import ContentSection from '@/components/ContentSection';
import CompactCard from '@/components/CompactCard';
import { extractPlainText } from '@/lib/lexical-parser';

export default function ArticlesPage() {
  const { language } = useAppLanguage();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Fetch categories dynamically from API
  const { data: categories = [] } = useCategoriesByType(language, 'articles');

  const { data: articles = [], isLoading, error } = useQuery({
    queryKey: ['articles', language],
    queryFn: () => articlesApi.getAll(language),
  });

  const filteredArticles = selectedCategory
    ? articles.filter((a) => a.category === selectedCategory)
    : articles;

  const featuredArticles = articles.filter((a) => a.featured);

  const getTitle = (article: { title: string; title_ru?: string }) =>
    language === 'ru' ? article.title_ru || article.title : article.title;

  const getSubtitle = (article: { subtitle?: string; subtitle_ru?: string }) =>
    language === 'ru' ? article.subtitle_ru || article.subtitle : article.subtitle;

  const getContent = (article: { content?: string; content_ru?: string; summary?: string }) =>
    language === 'ru' ? article.content_ru || article.content : article.content || article.summary;

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader title={getTranslation('articles', language)} />
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p>{getTranslation('loading', language) || 'Loading...'}</p>
        </ContentSection>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title={getTranslation('articles', language)} />
        <ContentSection style={{ padding: '24px', textAlign: 'center' }}>
          <p style={{ color: Colors.error }}>Error loading articles</p>
        </ContentSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader title={getTranslation('articles', language)} />

      {/* Featured Section */}
      {featuredArticles.length > 0 && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>{getTranslation('featured', language)}</h2>
          <div style={styles.horizontalScroll}>
            {featuredArticles.map((article) => (
              <div 
                key={article.id} 
                style={styles.featuredCard}
                onClick={() => navigate(`/articles/${article.id}`, { state: { fromApp: true } })}
              >
                <img src={article.image_url || article.imageUrl} alt={getTitle(article)} style={styles.featuredImage} />
                <div style={styles.featuredOverlay}>
                  <h3 style={styles.featuredTitle}>{getTitle(article)}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Category Filters */}
      <ContentSection style={{ ...styles.filtersContainer, padding: '0 24px' }}>
        <div style={styles.filterScroll}>
          <button
            style={{
              ...styles.filterChip,
              ...(selectedCategory === null ? styles.filterChipActive : {}),
            }}
            onClick={() => setSelectedCategory(null)}
          >
            <span style={selectedCategory === null ? styles.filterTextActive : styles.filterText}>
              {getTranslation('allArticles', language)}
            </span>
          </button>
          {categories.map((category) => (
            <button
              key={category.slug}
              style={{
                ...styles.filterChip,
                ...(selectedCategory === category.slug ? styles.filterChipActive : {}),
              }}
              onClick={() => setSelectedCategory(category.slug)}
            >
              <span
                style={
                  selectedCategory === category.slug ? styles.filterTextActive : styles.filterText
                }
              >
                {category.name}
              </span>
            </button>
          ))}
        </div>
      </ContentSection>

      {/* Articles List */}
      <ContentSection style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '0 24px' }}>
        {filteredArticles.length > 0 ? (
          filteredArticles.map((article) => (
            <CompactCard
              key={article.id}
              id={article.id}
              imageUrl={article.image_url || article.imageUrl}
              imageAlt={getTitle(article)}
              title={getTitle(article)}
              subtitle={getSubtitle(article)}
              description={`${extractPlainText(getContent(article) || '').substring(0, 100) || 'No description'}...`}
              promoted={!!article.featured}
              onCardClick={() => navigate(`/articles/${article.id}`, { state: { fromApp: true } })}
            />
          ))
        ) : (
          <p style={{ textAlign: 'center', color: Colors.textSecondary }}>
            {selectedCategory ? 'No articles in this category' : 'No articles found'}
          </p>
        )}
      </ContentSection>

      <div style={{ height: '40px' }} />
    </PageContainer>
  );
}

const styles = {
  section: {
    marginBottom: '24px',
  } as React.CSSProperties,

  sectionTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: Colors.text,
    padding: '0 24px',
    marginBottom: '16px',
    margin: 0,
  } as React.CSSProperties,

  horizontalScroll: {
    display: 'flex',
    gap: '16px',
    padding: '0 24px',
    overflowX: 'auto' as const,
    scrollBehavior: 'smooth' as const,
    width: '100%',
    boxSizing: 'border-box' as const,
  } as React.CSSProperties,

  featuredCard: {
    position: 'relative' as const,
    width: '280px',
    height: '200px',
    borderRadius: '16px',
    overflow: 'hidden',
    flexShrink: 0,
    cursor: 'pointer',
  } as React.CSSProperties,

  featuredImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  } as React.CSSProperties,

  featuredOverlay: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  } as React.CSSProperties,

  featuredTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: Colors.surface,
    margin: 0,
  } as React.CSSProperties,

  filtersContainer: {
    marginBottom: '24px',
    padding: '0 !important',
  } as React.CSSProperties,

  filterScroll: {
    display: 'flex',
    gap: '12px',
    overflowX: 'auto' as const,
    scrollBehavior: 'smooth' as const,
    justifyContent: 'flex-start',
  } as React.CSSProperties,

  filterChip: {
    padding: '8px 16px',
    borderRadius: '20px',
    backgroundColor: Colors.surface,
    border: `1px solid ${Colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '14px',
    fontWeight: '600',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  } as React.CSSProperties,

  filterChipActive: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  } as React.CSSProperties,

  filterText: {
    color: Colors.text,
  } as React.CSSProperties,

  filterTextActive: {
    color: Colors.surface,
  } as React.CSSProperties,
};
